
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApplicationCatalog } from "@/components/applications/ApplicationCatalog";
import { Button } from "@/components/ui/button";
import { ProfileAssignment } from "@/components/profiles/ProfileAssignment";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { appStoreCatalog, macOSCatalog } from "@/data/mockApps";
import { useApplications } from "@/hooks/useApplications";
import { Application } from "@/types/application";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function AddApplicationPage() {
  const navigate = useNavigate();
  const { addApplication } = useApplications();
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<'appStore' | 'macOSCatalog' | null>(null);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  
  const form = useForm();

  const handleSelectApp = (id: string, source: 'appStore' | 'macOSCatalog') => {
    setSelectedAppId(id);
    setSelectedSource(source);
  };

  const handleAddApplication = async () => {
    if (!selectedAppId || !selectedSource || selectedCustomers.length === 0) {
      toast.error("Please select an application and at least one customer");
      return;
    }

    const catalog = selectedSource === 'appStore' ? appStoreCatalog : macOSCatalog;
    const selectedApp = catalog.find(app => app.id === selectedAppId);
    
    if (!selectedApp) {
      toast.error("Selected application not found");
      return;
    }

    try {
      const newApp: Omit<Application, "id"> = {
        name: selectedApp.name,
        icon: selectedApp.icon,
        version: "1.0.0",
        size: "Unknown", 
        releaseDate: new Date().toISOString().split('T')[0],
        source: selectedSource,
        platform: selectedSource === 'appStore' ? 'ios' : 'macos',
        bundleId: `com.example.${selectedApp.name.toLowerCase().replace(/\s/g, '')}`,
        customers: selectedCustomers,
        status: 'draft',
      };

      await addApplication(newApp);
      navigate('/applications');
    } catch (error) {
      console.error("Failed to add application:", error);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/applications')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">Add Applications</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Application Catalog</CardTitle>
              <CardDescription>
                Select an application to add to your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ApplicationCatalog onSelectApp={handleSelectApp} />
            </CardContent>
          </Card>
        </div>
        
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Customer Assignment</CardTitle>
              <CardDescription>
                Assign this application to customers and groups
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <ProfileAssignment 
                  form={form}
                  setCustomers={setSelectedCustomers}
                  setGroups={setSelectedGroups}
                  multipleCustomers={true}
                />
              </Form>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button 
                disabled={!selectedAppId || selectedCustomers.length === 0}
                onClick={handleAddApplication}
              >
                Add Application
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}
